/**
 * Menu dish photos were uploaded to Firebase Storage (`menu_images/...`).
 * When the project's Storage billing account is closed, those download URLs
 * return HTTP 402 and SafeImage falls back to placeholders.
 *
 * Many of the same files also ship under Firebase Hosting (`public/eggstacy`,
 * `public/host`, `public/images`). This helper rewrites Storage/gs URLs to
 * those Hosting paths when a matching public asset exists.
 */

const PUBLIC_IMAGE_PATHS: readonly string[] = [
  "/addKitchen.png",
  "/bracket.png",
  "/brassring.png",
  "/default-food.svg",
  "/dial1.png",
  "/divcafe-logo.png",
  "/eggstacy-logo.png",
  "/eggstacy/boiled-egg-slices.png",
  "/eggstacy/boiled-eggs-uncut.png",
  "/eggstacy/bread-omlette.png",
  "/eggstacy/bun-omlette.png",
  "/eggstacy/cheese-omlette.png",
  "/eggstacy/cheesy-omlette.png",
  "/eggstacy/egg-bajji.png",
  "/eggstacy/egg-batani-chat.png",
  "/eggstacy/egg-carrot-cube.png",
  "/eggstacy/egg-omlette-cheese-tomato.png",
  "/eggstacy/egg-omlette-tomato-chilli.png",
  "/eggstacy/egg-spinach-cube.png",
  "/eggstacy/egg-whites.png",
  "/eggstacy/eggbhurji.png",
  "/eggstacy/eggwhites.png",
  "/eggstacy/french-toast.png",
  "/eggstacy/goldenegg-diced.png",
  "/eggstacy/goldenegg.jpeg",
  "/eggstacy/myo-omlette-tomato.png",
  "/eggstacy/plain-omlette-full.png",
  "/eggstacy/plain-omlette.png",
  "/eggstacy/sliced-golden-egg.jpg",
  "/eggstacy/sun-flower-egg.png",
  "/fingerstop.png",
  "/food-hero-svg.png",
  "/host/Dosa.png",
  "/host/idly.png",
  "/host/paper-dosa.png",
  "/host/set-dosa.png",
  "/host/vada.png",
  "/images/Ruchira-Banner.png",
  "/images/Ruchira-Flat-Logo.png",
  "/images/bachelor-marinated-phone.png",
  "/images/bubble.png",
  "/images/butter-cooked (2).png",
  "/images/butter-marinated-phone.png",
  "/images/buttonbg.png",
  "/images/cart.png",
  "/images/dial.png",
  "/images/fingerstop.png",
  "/images/knob.png",
  "/images/mmh-banner.jpeg",
  "/images/mmh-logo.png",
  "/images/modalbg.png",
  "/images/mughlai-cooked-phone.png",
  "/images/mughlai-marinated-phone.png",
  "/images/navbg.png",
  "/images/nizam's chicken-cooked (Phone).png",
  "/images/nizam's chicken-cooked.png",
  "/images/nizam's chicken-marinated (Phone).png",
  "/images/nizam's chicken-marinated.png",
  "/images/pagebg.png",
  "/images/toggle.png",
  "/images/toggleslot.png",
  "/images/xarvin.png",
  "/kitchenbg.png",
  "/nameplate.png",
  "/nanduskitchen-logo.png",
  "/placeholder-food.svg",
  "/ruchira-bw.svg.jpg",
  "/ruchira-logo.jpg",
  "/ruchira-logo.png",
  "/selectorbutton.png",
];

const PUBLIC_BY_BASENAME = new Map<string, string>();
for (const p of PUBLIC_IMAGE_PATHS) {
  const base = p.split("/").pop()!.toLowerCase();
  // Prefer kitchen-specific folders over root when basenames collide
  if (!PUBLIC_BY_BASENAME.has(base) || p.includes("/eggstacy/") || p.includes("/host/") || p.includes("/images/")) {
    PUBLIC_BY_BASENAME.set(base, p);
  }
}

function stripUploadPrefix(filename: string): string {
  // "1769507971374_boiled-eggs-uncut.png" -> "boiled-eggs-uncut.png"
  const tsPrefix = filename.match(/^\d{10,}_(.+)$/);
  if (tsPrefix) return tsPrefix[1];
  // "egg-batani-chat.png_1763361196367" -> "egg-batani-chat.png"
  const tsSuffix = filename.match(/^(.+\.(?:png|jpe?g|webp|gif|svg))_\d{10,}$/i);
  if (tsSuffix) return tsSuffix[1];
  return filename;
}

/** Extract object filename from a Firebase Storage download URL or gs:// path. */
export function extractStorageFilename(url: string): string | null {
  const s = String(url || "").trim();
  if (!s) return null;

  if (s.startsWith("gs://")) {
    const withoutScheme = s.slice("gs://".length);
    const slash = withoutScheme.indexOf("/");
    if (slash < 0) return null;
    return withoutScheme.slice(slash + 1).split("/").pop() || null;
  }

  try {
    const u = new URL(s);
    const marker = "/o/";
    const idx = u.pathname.indexOf(marker);
    if (idx >= 0) {
      const encoded = u.pathname.slice(idx + marker.length);
      const decoded = decodeURIComponent(encoded);
      return decoded.split("/").pop() || null;
    }
  } catch {
    // not a URL
  }
  return null;
}

function isFirebaseStorageUrl(url: string): boolean {
  return (
    /firebasestorage\.googleapis\.com/i.test(url) ||
    /^gs:\/\//i.test(url) ||
    /\.firebasestorage\.app/i.test(url)
  );
}

/**
 * If `url` is a Storage download/gs path whose basename matches a Hosting
 * public asset, return that site-relative path. Otherwise return the original
 * (trimmed) url, or undefined when empty.
 */
export function resolveMenuImageUrl(
  url?: string | null
): string | undefined {
  if (url == null) return undefined;
  const trimmed = String(url).trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") return undefined;

  if (
    trimmed.startsWith("/") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:")
  ) {
    return trimmed;
  }

  if (!isFirebaseStorageUrl(trimmed)) {
    return trimmed;
  }

  const filename = extractStorageFilename(trimmed);
  if (!filename) return trimmed;

  const stripped = stripUploadPrefix(filename);
  const hit =
    PUBLIC_BY_BASENAME.get(stripped.toLowerCase()) ||
    PUBLIC_BY_BASENAME.get(filename.toLowerCase());

  return hit || trimmed;
}

/** Local Hosting candidates to try after a Storage URL fails to load. */
export function localMenuImageCandidates(url?: string | null): string[] {
  if (!url) return [];
  const trimmed = String(url).trim();
  if (!isFirebaseStorageUrl(trimmed)) return [];
  const filename = extractStorageFilename(trimmed);
  if (!filename) return [];
  const stripped = stripUploadPrefix(filename);
  const bases = Array.from(new Set([stripped, filename]));
  const dirs = ["eggstacy", "host", "images"];
  const out: string[] = [];
  for (const base of bases) {
    const known = PUBLIC_BY_BASENAME.get(base.toLowerCase());
    if (known) out.push(known);
    for (const dir of dirs) {
      out.push(`/${dir}/${base}`);
    }
    out.push(`/${base}`);
  }
  return Array.from(new Set(out));
}

/** Deep-resolve photoUrl fields on a menu item (and nested customizations). */
export function resolveItemPhotoUrls<T extends Record<string, any>>(item: T): T {
  if (!item || typeof item !== "object") return item;

  const next: any = { ...item };
  if ("photoUrl" in next) {
    next.photoUrl = resolveMenuImageUrl(next.photoUrl) ?? next.photoUrl;
  }
  if ("imageUrl" in next) {
    next.imageUrl = resolveMenuImageUrl(next.imageUrl) ?? next.imageUrl;
  }

  if (Array.isArray(next.customizations)) {
    next.customizations = next.customizations.map((level: any) => {
      if (!level || typeof level !== "object") return level;
      return {
        ...level,
        options: Array.isArray(level.options)
          ? level.options.map((opt: any) => resolveItemPhotoUrls(opt))
          : level.options,
      };
    });
  }

  return next as T;
}
